/** @module csi/heading/_base */
define([
	"dojo/_base/lang",
	"dojo/_base/NodeList",
	"dojo/dom-class",
	"dojo/json",
	"dojo/keys",
	"dojo/query",
	"csi",
	"csi/_base/underscore",
	"csi/action",
	"csi/html",
	"csi/locale",
	"csi/middleware/rollup/rollupService",
	"csi/store/Cache",
	"csi/types",
	"csi/utility/coreUtility",
	"csi/utility/validateUtility",
	"csi/xml",
	"./Spec",
], function(lang, NodeList, domClass, json, keys, query, csi, _, csiAction, html, locale, {validateValue}, CacheStore, types, {isDefined},validateUtility, xml, Spec) {

	const {FORMAT_IMAGE, FORMAT_REFERENCED_IMAGE, FORMAT_TAG} = types;

	html.registerDataAttribute("DATA_HEADING", "heading");
	html.registerDataAttribute("DATA_HEADING_NOTAB", "heading-nt", Boolean);
	html.registerDataAttribute("DATA_HEADING_READ_ONLY", "heading-ro", Boolean);

	// if key is a number, then will use DATA_HEADING_VALUE_INDEX, otherwise DATA_HEADING_VALUE_KEY
	html.registerDataAttribute("DATA_HEADING_VALUE_INDEX", "heading-vi", Number);
	html.registerDataAttribute("DATA_HEADING_VALUE_KEY", "heading-vk");

	csiAction.registerControllers("", {
		ActionBoolean: "csi.heading.BooleanController",
		ActionBooleanRef: "csi.heading.BooleanRefController",
		ActionEnum: "csi.heading.EnumController",
		ActionNumber: "csi.heading.NumberController",
		ActionString: "csi.heading.TextController",
		ActionStringRich: "csi.heading.StringRichController",
		ActionStringSelection: "csi.heading.SelectController",
		ActionStringURL: "csi.heading.StringURLController",
		ActionTime: "csi.heading.TimeController",
		AggregateAttribute: "csi.heading.AggregateAttributeController",
		AggregateInline: "csi.heading.AggregateController",
		DisableExpression: "csi.heading.DisableExpressionController",
		FullSizeImage: "csi.image.LightGalleryController",
		RestoreExpression: "csi.heading.RestoreExpressionController"
	});

	const VALID_ALPHANUMERIC = "ValidateAlphanumeric";
	const VALID_EMAIL = "ValidateEmail";
	const VALID_URL = "ValidateURL";
	const VALID_INTEGER = "ValidateInteger";
	const VALID_NUMERIC = "ValidateNumeric";
	const VALID_NUMERIC_SIZE = "ValidateNumericSize";
	const VALID_REQUIRED = "ValidateRequired";
	let useEditableLargeIcons = false;

	// Object: factory of attribute renderer classes based on attribute type and format
	//	{
	//		"attribute type": {
	//			"": "action class for type independent of format",
	//			"format": "action class for type w/ format"
	//		}
	//	}
	var _factoryRenderers = {
		base: {
			"": "csi/heading/Renderer"
		},
		"boolean": {
			"": "csi/heading/Boolean"
		},
		cnl: {
			"": "csi/heading/Ref"
		},
		"double": {
			"": "csi/heading/Number"
		},
		"enum": {
			"": "csi/heading/Enum"
		},
		"enumlist": {
			"": "csi/heading/EnumContainer"
		},
		"enumset": {
			"": "csi/heading/EnumContainer"
		},
		"error": {
			"": "csi/heading/Error"
		},
		"expr": {
			"": "csi/heading/Expression"
		},
		"expression": {
			"": "csi/heading/String"
		},
		"float": {
			"": "csi/heading/Number"
		},
		"integer": {
			"": "csi/heading/Number"
		},
		int64: {
			"": "csi/heading/Number",
			"Hex": "csi/heading/Hex"
		},
		"long": {
			"": "csi/heading/Number"
		},
		"ref": {
			"": "csi/heading/Ref",
			[FORMAT_IMAGE]: "csi/heading/RefImage",
		},
		reflist: {
			"": "csi/heading/RefContainer",
			[FORMAT_IMAGE]: "csi/heading/RefImageContainer",
			[FORMAT_REFERENCED_IMAGE]: "csi/heading/RefImageOnContainer",
		},
		refmap: {
			"": "csi/heading/RefContainer",
			[FORMAT_IMAGE]: "csi/heading/RefmapImage",
			[FORMAT_REFERENCED_IMAGE]: "csi/heading/RefImageOnContainer",
			[FORMAT_TAG]: "csi/heading/RefTagContainer",
		},
		refset: {
			"": "csi/heading/RefContainer",
			[FORMAT_IMAGE]: "csi/heading/RefImageContainer",
			[FORMAT_REFERENCED_IMAGE]: "csi/heading/RefImageOnContainer",
		},
		refvector: {
			"": "csi/heading/Refvector",
			[FORMAT_IMAGE]: "csi/heading/RefImageContainer",
			[FORMAT_REFERENCED_IMAGE]: "csi/heading/RefImageOnContainer",
		},
		"string": {
			"": "csi/heading/String",
			Alphanumeric: "csi/heading/StringAlphanumeric",
			Barcode: "csi/heading/StringBarcode",
			Comment: "csi/heading/StringComment",
			CommentLatest: "csi/heading/StringCommentLatest",
			Email: "csi/heading/StringEmail",
			Phone: "csi/heading/StringPhone",
			Rich: "csi/heading/StringRich",
			Selection: "csi/heading/StringSelection",
			Textarea: "csi/heading/StringTextarea",
			Type: "csi/heading/StringType",
			URL: "csi/heading/StringURL"
		},
		"stringmap": {
			"": "csi/heading/StringContainer",
			Localized: "csi/heading/LocalizedString",
			LocalizedTextarea: "csi/heading/LocalizedTextarea",
			LocalizedRich: "csi/heading/LocalizedRich",
		},
		stringlist: {
			"": "csi/heading/StringContainer"
		},
		stringset: {
			"": "csi/heading/StringContainer"
		},
		stringvector: {
			"": "csi/heading/Stringvector"
		},
		time: {
			"": "csi/heading/Time",
			"Date": "csi/heading/Time",
			"DateTime": "csi/heading/TimeDateTime",
			"DateTimeSeconds": "csi/heading/TimeDateTime",
			"Diff": "csi/heading/TimeDiff",
			"Time": "csi/heading/TimeOnly",
			"UniversalDate": "csi/heading/UniversalDate",
			"UniversalTime": "csi/heading/UniversalTime"
		},
		"url": {
			"": "csi/heading/Url",
			[FORMAT_IMAGE]: "csi/heading/UrlImage"
		},
		urlmap: {
			"": "csi/heading/UrlMap"
		},

		// Node Properties
		Type: {
			"": "csi/heading/Property"
		},
		FG: {
			"": "csi/heading/Property"
		},
		URL: {
			"": "csi/heading/Property"
		},
		Name: {
			"": "csi/heading/PropertyName"
		},
		CT: {
			"": "csi/heading/PropertyTime"
		},
		PT: {
			"": "csi/heading/PropertyTime"
		},
		CR: {
			"": "csi/heading/PropertyRef"
		},
		PB: {
			"": "csi/heading/PropertyRef"
		}
	};

	var _factoryInstances = {};

	function isHeadingNodeTabStop(/*DOMNode*/refNode) {
		// summary: return if element can be navigated to by keyboard
		return html.getDataAttribute(refNode, html.DATA_HEADING) &&
			!html.getDataAttribute(refNode, html.DATA_HEADING_NOTAB) &&
			html.getElementAction(refNode, true);
	}

	function getNextTabStop(/*DOMNode*/rootNode, /*DOMNode*/currentNode, /*String*/key, /*Boolean*/bShift) {
		// summary: iterate DOM nodes from one to the next based on key input (supported: tab, enter)
		var firstName;
		var nextName;
		var nextNode;

		function getNextElement(domNode, bSkip) {
			if (!domNode)
				return null;

			if (!bSkip) {
				// return node if we have reached one
				if ((domNode == currentNode) || isHeadingNodeTabStop(domNode))
					return domNode;

				// traverse children
				if (!html.getDataAttribute(domNode, html.DATA_HEADING)) {
					var e = domNode[firstName];

					while (e) {
						if (e2 = getNextElement(e))
							return e2;

						e = e[nextName];
					}
				}
			}

			// if no children of view div satisfy domNode, then return
			if (domNode == rootNode)
				return null;

			// find next sibling to traverse
			while (!domNode[nextName]) {
				domNode = domNode.parentNode;

				// if we reached the view root, then start with the first domNode of the view
				if (domNode == rootNode)
					return getNextElement(rootNode);
			}

			return getNextElement(domNode[nextName]);
		}

		if (!rootNode) {
			return null;
		}

		if ((key == keys.TAB) || (key == keys.ENTER)) {
			firstName = bShift ? "lastChild" : "firstChild";
			nextName = bShift ? "previousSibling" : "nextSibling";
			nextNode = getNextElement(currentNode, true);
		}

		// return new domNode only if different than initial domNode
		return (nextNode == currentNode) ? null : nextNode;
	}

	return {
		// Constants
		CSS_COMMENT: "iconCellComment",
		CSS_EDITABLE: "iconEditable",
		CSS_LARGE_EDITABLE: "csi-large-editable-icons",
		CSS_EDITABLE_EXP: "iconEditableExp",
		CSS_EDITABLE_OVER: "iconEditableOver",
		CSS_EDITING: "actionEdit",
		CSS_ERROR: "attrError",
		CSS_PRIMARY: "attrPrimary",
		CSS_PROGRESS_CONTAINER: "progressContainer",

		CTX_LARGE_FORMAT: "headingLargeFormat",

		QUERY_OPS: {
			"boolean": ["EQ", "NE"],
			"container": ["CO", "NC"],
			"double": ["EQ", "NE", "GT", "GE", "LT", "LE"],
			"enum": ["EQ", "NE", "CO", "NC"],
			"enumcontainer": ["CO", "NC"],
			"ref": ["EQ", "NE", "CO", "NC", "RE", "NM"],
			"refcontainer": ["CO", "NC", "RE", "NM"],
			"string": ["EQ", "NE", "RE", "NM"],
			"stringcontainer": ["CO", "NC", "RE", "NM"],
			"time": ["EQ", "RANGE", "WITHIN", "GT", "GE", "LT", "LE"]
		},

		// determines if comparison operators are allowed on text attributes
		allowTextComparisonOperators: false,

		// determines if filters restore single select for string type with no format and text format
		restoreSelectInputForEqualsOperators: false,

		// Constructors for container and default renderers
		containerRendererConstructor: null,
		defaultRendererConstructor: null,

		getNextTabStop,
		isHeadingNodeTabStop,

		registerRenderers: function(/*Object*/obj) {
			// summary: register more attribute type and format actions
			lang.mixin(_factoryRenderers, obj);
		},

		/** clear renderer cache */
		clearRendererCache() {
			_factoryInstances = {};
		},

		isHeadingNodeEditable: function(/*DOMNode*/refNode) {
			// summary: return true if cell is editable
			return domClass.contains(refNode, this.CSS_EDITABLE);
		},

		/**
		 * Render information for restoring expression
		 * @param {Tooltip} tooltip
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {Renderer} ren
		 * @param {Node} reportNode - node with attribute value if the expression were restored
		 */
		addRestoreExpressionToTooltip: function(tooltip, ctx, h, ren, reportNode) {
			var div = html.create("div");
			var span1 = html.create("span", null, div);
			var span2 = html.create("span", null, div);
			var cacheOrig = ctx.cache;

			// if reportNode given, create new cache for context to use while rendering expression value
			if (reportNode) {
				ctx.cache = new CacheStore();
				ctx.cache.put(reportNode);
			}

			html.setInnerText(span1, locale.resource("RestoreExpression_Value"));

			if (!ren.toHtmlStatic(ctx, h, span2)) {
				html.text(span2, locale.getEmptyName());
			}

			tooltip.addContent(div);

			// restore context
			ctx.cache = cacheOrig;
		},

		applyHeadingRenderer: function(/*Context*/ctx, /*Heading*/h, /*Function*/callback) {
			// summary: apply function to renderer in context with prototype:
			//		function(/*Renderer*/renBase, /*Heading*/hBase)

			// first, apply path to context
			var keyOrig = ctx.keyValue;
			var path = ctx.bReporting ? h.getQueryPath() : h.m_path;
			var ren, res;

			if (!ctx.push(path)) {
				// if context cannot be initialized, allow renderer classes opportunity to do it themselves
				if (h.classRenderer) {
					if (ren = this.getHeadingRenderer(null, h)) {
						if (!ren.pushContext(ctx, h)) {
							ren = null;
						}
					}
				}

				if (!ren) {
					return callback(null, h);
				}
			}

			if (ren = ren || this.getHeadingRenderer(ctx, h)) {
				// apply heading index (if any) to context
				if (isDefined(h.m_index) && !ctx.hasContainerKey()) {
					ctx.keyValue = h.m_index;
				}

				// if renderer found, apply any changes to context necessary
				ren.reserveContext(ctx, h);

				// process attribute expression if context has rollup processor
				if (h.hasRollupExpression()) {
					ctx.rp?.evaluateAttribute(h.m_id, ctx.getNode());
				}

				// apply callback function
				var baseRen = ren.getBaseRenderer(ctx, h);
				var baseHeading = ren.getBaseHeading(ctx, h);
				res = callback(baseRen, baseHeading);

				// release any changes to context
				ren.releaseContext(ctx, h);
			} else {
				// if renderer not found, then revert any changes to context
				res = callback(null, h);
				ctx.pop();
			}

			ctx.keyValue = keyOrig;
			return res;
		},

		/**
		 * Return renderer class instance that corresponds to given type, format, and params
		 * @param {string} type
		 * @param {string} [format]
		 * @param {Object} [params] - optional parameters to apply to renderer class instance
		 * @return {Renderer}
		 */
		getRenderer: function(type, format, params) {
			type = type || "";
			format = format || "";

			// use registered class for type, format and params
			var obj = _factoryRenderers[type];
			if (obj) {
				return this.getClassRenderer(obj[format] || obj[""], type, format, params);
			}

			const key = type + format + (params ? json.stringify(params) : "");
			let ren = _factoryInstances[key];
			if (!ren) {
				var base = xml.attributeTypeGetBase(type);
				if (base) {
					// if type is container, use Container renderer in combination with individual renderer for base type
					ren = new this.containerRendererConstructor(type, format);
					ren._setBaseRenderer(this.getRenderer(base, format));
				} else {
					// use default renderer
					ren = new this.defaultRendererConstructor(type, format);
					console.warn("getRenderer: no registered renderer for type: " + type);
				}

				if (params) {
					_.extend(ren, params);
				}

				_factoryInstances[key] = ren;
			}

			return ren;
		},

		getClassRenderer: function(/*String|Function*/cls, /*String?*/type, /*String?*/format, /*Object?*/params) {
			// summary: return renderer instance with given class name
			var ctr;

			if (typeof cls !== "string") {
				ctr = cls;
				cls = ctr._clsid;

				if (!cls) {
					// assign unique id so that this renderer can be identified again - declaredClass does not work
					// when renderer is not in its own module
					cls = ctr._clsid = csi.getUniqueId();
				}
			}

			// if params specified, track instances with params
			const rendererParams = {
				...params,
				format,
				type,
			};

			const key = cls + json.stringify(rendererParams);
			let ren = _factoryInstances[key];

			if (!ren) {
				// get constructor from class/module name - the class must have already been required by now
				// for this to work
				if (!ctr) {
					ctr = csi.syncRequire(cls);
				}

				// create and store instance instead of class name so it can be reused if not already created
				if (ctr) {
					// if class specified as base, then create renderer and use itself as base
					var base = ctr.prototype.bBaseRenderer ? xml.attributeTypeGetBase(type) : null;

					ren = new ctr(base || type, format);

					if (params) {
						_.extend(ren, params);
					}
					_factoryInstances[key] = ren;

					if (!ren.renBase) {
						// assign base renderer if renderer type is container attribute
						var renBase;

						if (base) {
							renBase = ren;
						} else if (base = xml.attributeTypeGetBase(ren.type)) {
							renBase = this.getRenderer(base, format);
						}

						if (renBase) {
							ren._setBaseRenderer(renBase);
						}
					}
				}
			}

			return ren;
		},

		getHeadingRenderer: function(/*Context?*/ctx, /*Heading*/h) {
			// summary: return Renderer for heading - either using given context or reflection model to get type
			var cls = h.classRenderer;
			var params = h.classRendererParams;
			var type = h.getAttrType();
			var bReporting = ctx && ctx.bReporting;
			var ren;

			// get true attribute type from current node
			if (ctx) {
				type = xml.nodeAttributeGetType(ctx.getNode(), h.m_id) || type;
			}

			if (cls) {
				// instantiate explicit class renderer
				ren = this.getClassRenderer(cls, type, h.m_format, params);

				// most renderer classes are not used for report rows, but there are some exceptions
				if (ren && (!bReporting || ren.bAllowReporting)) {
					return ren;
				}
			}

			if (h.m_value && (!h.m_id || !bReporting)) {
				// real-time Value should override rollup Id, unless reporting
				ren = this.getRenderer("expr", h.m_format, params);
			} else if (h.m_prop) {
				ren = this.getRenderer(h.m_prop, h.m_format, params);
			} else if (h.m_id && type) {
				ren = this.getRenderer(type, h.m_format, params);
			} else if (!h.m_id && h.m_onDraw) {
				ren = this.getRenderer("base", null, params);
			}

			return ren;
		},

		getHeadingRendererInContext: function(/*Context*/ctx, /*Heading*/h) {
			// summary: get renderer for heading - if context has key, then return base renderer
			var ren = this.getHeadingRenderer(ctx, h);
			return ren ? ren.getBaseRenderer(ctx, h) : null;
		},

		/**
		 * Create QuerySpec for given Spec and Context; the Context is solely used to pass view settings rather than node data
		 * @param {Context} ctx
		 * @param {Spec} spec
		 * @return {QuerySpec}
		 */
		getQuerySpec: function(ctx, spec) {
			var ren = this.getHeadingRenderer(null, spec.heading);
			return ren ? ren.getQuerySpec(ctx, spec.heading, spec) : null;
		},

		/**
		 * Convert filter value to text - value should not be an array, but just an individual filter value
		 * @param {Context} ctx
		 * @param {Spec} spec
		 * @param {any} value
		 * @return {string}
		 */
		getTextForFilterValue: function(ctx, spec, value) {
			if ((value == xml.VALUE_BLANK) || (value == xml.VALUE_NONBLANK)) {
				return locale.resource(value);
			}

			if (spec != null && value != null) {
				value = spec.querySpec.ren.formatQueryFilterValue(ctx, value, spec.format);
			}

			return (value == null) ? "" : String(value);
		},

		/**
		 * Convert value based on client filter
		 * @param {Context} ctx
		 * @param {Spec} spec
		 * @param {any} value
		 * @return {any}
		 */
		getClientFilterValue: function(ctx, spec, value) {
			return spec?.querySpec.ren?.getClientFilteredValue(ctx, spec, value) ?? value;
		},

		clearHtml: function(/*DOMNode*/refNode) {
			// summary: delete DOM corresponding to attribute value - may leave behind view-specific DOM content in cell;
			// this is determined by looking for element with "attrPrimary" class which should only exist as direct child of refNode
			var primary = query("." + this.CSS_PRIMARY, refNode)[0];
			if (primary && (primary != refNode.firstChild) && (primary.parentNode == refNode)) {
				html.destroyRange(primary, refNode.lastChild);
			} else {
				html.destroyChildren(refNode);
			}

			// make sure to clear action on cell in case it should no longer be editable
			html.removeDataAttribute(refNode, html.DATA_ACTION);
		},

		refreshHtml: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			// summary: refresh existing content with new heading content
			var ren = this.getHeadingRenderer(ctx, h);

			// since this does not go through applyHeadingRenderer, need to check for expressions here too
			if (h.hasRollupExpression()) {
				ctx.rp?.evaluateAttribute(h.m_id, ctx.getNode());
			}

			this.clearHtml(refNode);
			this.toHtmlInContext(ctx, refNode, ren ? ren.getBaseRenderer(ctx, h) : null, ren ? ren.getBaseHeading(ctx, h) : null);

			return ren;
		},

		toFilterTextAndValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return text value for Heading; if value is an array, then text will be an array as well
			return this.applyHeadingRenderer(ctx, h, function(renBase, hBase) {
				var value = renBase && renBase.toFilterValue(ctx, hBase);
				var text = renBase && renBase.formatValueForFiltering(ctx, hBase, value);
				return {text: text, value: value};
			});
		},

		toFilterValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return text value for Heading
			return this.applyHeadingRenderer(ctx, h, function(renBase, hBase) {
				return renBase ? renBase.toFilterValue(ctx, hBase) : "";
			});
		},

		toHtml: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			// summary: render heading Html under given refNode
			return this.applyHeadingRenderer(ctx, h, lang.hitch(this, this.toHtmlInContext, ctx, refNode));
		},

		toHtmlInContext: function(/*Context*/ctx, /*DOMNode*/refNode, /*Renderer*/ren, /*Heading*/h) {
			// summary: render heading in context
			if (ren) {
				try {
					ren.headingToHtml(ctx, h, refNode);
				} catch (ex) {
					console.error("Renderer error: ", ex);
					html.setInnerText(refNode, locale.resource("ERROR"));
					domClass.add(refNode, this.CSS_ERROR);
				}
			}

			return ren;
		},

		/**
		* Returns the viewable source for a path to an image
		* @param {Context} ctx
		* @param {Heading} h
		* @param {object} imageDimensions - image dimensions object whose width and height will be set
		* @returns {string}
		*/
		toImageViewable: function(ctx, h, imageDimensions) {
			return this.applyHeadingRenderer(ctx, h, function(renBase, hBase) {
				if (!renBase || !renBase.toViewable) {
					return "";
				}

				if (imageDimensions) {
					var imageContainer = ctx.getNode();
					var value = imageContainer[h.m_id];

					if (h.m_index != null) {
						value = value[h.m_index];
					}

					var imageNode = ctx.cache.get(value);

					if (imageNode) {
						imageDimensions.h = imageNode.Height;
						imageDimensions.w = imageNode.Width;
					}
				}

				return renBase.toViewable(ctx, hBase);
			});
		},

		toText: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return single text value for Heading; container attributes will be joined with separator
			return this.applyHeadingRenderer(ctx, h, function(renBase, hBase) {
				return renBase ? renBase.headingToText(ctx, hBase) : "";
			});
		},

		toComment: function(/*Context*/ctx, /*DOMNode*/refNode, /*String*/value) {
			// summary: render comment indicator for node
			domClass.add(refNode, this.CSS_COMMENT);
		},

		format: function(/*String*/type, /*String*/fmt, /*Any*/value) {
			// summary: get renderer based on type and format with hints to attribute id or node property
			var ren = this.getRenderer(type, fmt);
			return (ren && ren.formatValue(value, fmt)) || "";
		},

		/**
		 * set to use large version of "editable" icons
		 */
		useLargeIcons() {
			useEditableLargeIcons = true;
		},

		/**
		 * return additional css class for large icon "editable" if configured
		 * @return {string}
		 */
		getLargeEditableCSS() {
			return useEditableLargeIcons ? " " + this.CSS_LARGE_EDITABLE : "";
		},

		/**
		 * return additional css class for large icon "editable" if configured
		 * @param {HTMLElement} refNode
		 */
		addHeadingNodeEditableStyling(refNode) {
			domClass.add(refNode, this.CSS_EDITABLE + this.getLargeEditableCSS());
		},

		markHeadingEditable: function(/*DOMNode*/refNode, /*Heading*/h, /*Action?*/action) {
			// summary: mark DOM node as editable for heading; if action not specified, then default to editing using render action
			html.setElementAction(refNode, action ? action.m_id : h.getKeyDP());
			this.addHeadingNodeEditableStyling(refNode);
		},

		markHeadingNode: function(/*DOMNode*/refNode, /*Heading*/h, /*Context*/ctx) {
			// summary: mark DOM node with attributes to support rendering and editing
			if (ctx.bExcel) {
				return;
			}

			// mark DOMNode with current url - if heading has special renderer, then mark with
			// previous url before path applied in case renderer needs it (not needed for base renderers)
			ctx.applyNodeContext(refNode, true);

			// set read-only flag - necessary for tooltip actions even if cell not editable
			if (ctx.isReadOnly()) {
				html.setDataAttribute(refNode, html.DATA_HEADING_READ_ONLY, true);
			}

			// mark heading with key and label (label is for automation only)
			html.setDataAttribute(refNode, html.DATA_HEADING, h.getKeyDP());
			html.setDataAttribute(refNode, html.DATA_AUTOMATION, h.getLabel());
		},

		unmarkHeadingNode: function(/*DOMNode*/refNode) {
			// summary: unmark DOM
			html.removeDataAttribute(refNode, html.DATA_HEADING);
			domClass.remove(refNode, this.CSS_EDITABLE);
		},

		findHeadingNodes: function(/*DOMNode*/rootNode, /*Heading*/h, /*String*/urlNode, /*String*/keyValue) {
			// summary: find all nodes marked with given heading under given root node
			var key = h ? h.getKeyDP() : null;
			var keyNode = this.getHeadingNodeMarker(rootNode);
			var nl;

			if ((!key && keyNode) || (key && (key == keyNode))) {
				nl = new NodeList();
				nl.push(rootNode);
				return nl;
			}

			nl = query(html.getDataAttributeSelector(html.DATA_HEADING, key), rootNode);
			if (nl && urlNode) {
				nl = nl.filter(function(refNode) {
					// filter out nodes that are fake heading markers -- those that could be result of copy/paste into rich text editor
					if (!html.getDataAttribute(refNode, html.DATA_HEADING)) return false;

					// filter by desired URL and Index
					return ((html.getElementContext(refNode) == urlNode) && ((keyValue == null) || (this.getHeadingValueKey(refNode) == keyValue)));
				}, this);
			}

			return nl;
		},

		findParentHeadingNode: function(/*DOMNode*/e, /*DOMNode?*/rootNode) {
			// summary: return element with heading marker
			return html.getNodeWithDataAttribute(e, html.DATA_HEADING, rootNode);
		},

		setHeadingValueKey: function(/*DOMNode*/refNode, /*String*/key) {
			// summary: get index for editing single value in container attribute; use different attribute if number
			// so that a number will be returned on get
			if (_.isNumber(key)) {
				html.setDataAttribute(refNode, html.DATA_HEADING_VALUE_INDEX, key);
			} else {
				html.setDataAttribute(refNode, html.DATA_HEADING_VALUE_KEY, (key == null) ? "null" : key);
			}
		},

		getHeadingValueKey: function(/*DOMNode*/refNode) {
			// summary: get index for editing single value in container attribute
			var key = html.getDataAttribute(refNode, html.DATA_HEADING_VALUE_INDEX, true);
			if (key != null) {
				return key;
			}

			key = html.getDataAttribute(refNode, html.DATA_HEADING_VALUE_KEY, true);
			return (key === "null") ? null : key;
		},

		getHeadingNodeMarker: function(/*DOMNode*/refNode, /*Context?*/ctx) {
			// summary: return key for heading node
			var marker = html.getDataAttribute(refNode, html.DATA_HEADING);
			if (marker && ctx) {
				// adjust context parameters based on parameters stored with heading node
				ctx.readNodeContext(refNode);
				ctx.keyValue = this.getHeadingValueKey(refNode);

				if (html.getDataAttribute(refNode, html.DATA_HEADING_READ_ONLY, true)) {
					ctx.setReadOnly(true);
				}
			}

			return marker;
		},

		isEditing: function(/*DOMNode?*/refNode) {
			// summary: determine if refNode for heading is currently in edit mode
			if (refNode && domClass.contains(refNode, this.CSS_EDITING)) {
				return true;
			}

			return query("." + this.CSS_EDITING, refNode).length == 1;
		},

		disableHeadingTabStop: function(/*DOMNode*/refNode) {
			// summary: do not allow tab editing for given node
			html.setDataAttribute(refNode, html.DATA_HEADING_NOTAB, true);
		},

		validateValue: function(/*Any*/value, /*String*/type, /*String?*/format, /*Boolean*/bRequired, /*Attribute?*/rca, /*Boolean*/bSubset) {
			// summary: validate value of given type and format - if bSubset is false, then value may not be complete and
			// should not be ruled invalid if entering more characters could make it valid
			var s = String(value);
			var check = function(cond, idRes) {
				if (cond) {
					throw new Error(locale.resource(idRes));
				}
			};

			// check for required value
			if (bRequired) {
				check(!xml.valueIsValid(value, type), VALID_REQUIRED);
			}

			// check number types
			if (xml.attributeTypeIsInteger(type)) {
				check(isNaN(value) || !validateUtility.isInteger(s), VALID_INTEGER);
				check(validateUtility.isIntegerOutOfBounds(value), VALID_NUMERIC_SIZE);
			} else if (xml.attributeTypeIsNumeric(type)) {
				check((isNaN(value) || !validateUtility.isNumeric(s)), VALID_NUMERIC);
				check(validateUtility.isNumericOutOfBounds(value), VALID_NUMERIC_SIZE);
			}

			// check string formats
			if (s) {
				check((format == types.FORMAT_ALPHANUMERIC) && !validateUtility.isAlphaNumeric(s), VALID_ALPHANUMERIC);
				check((format == types.FORMAT_INTEGER) && (isNaN(value) || !validateUtility.isInteger(s)), VALID_INTEGER);

				if (!bSubset) {
					check((format == types.FORMAT_EMAIL) && !validateUtility.isEmailAddress(s), VALID_EMAIL);
					check((format == types.FORMAT_URL) && !validateUtility.isUrl(s), VALID_URL);
					check((format == types.FORMAT_PHONE) && !validateUtility.isPhone(s), locale.VALIDATE_VALUE);
				}
			}

			// check the validation expression on the client
			if (!bSubset && rca && rca.getValidation()) {
				var typeNode = rca.getOwner().getType();
				var bValid = validateValue(value, rca.getValidation());

				if (bValid === false) {
					throw new Error(locale.getValidationError(typeNode, rca.getId()));
				}

				return locale.getValidationKey(typeNode, rca.getId());
			}
		}
	};
});
